/**
 * useCart — store Zustand para el carrito (bolsa) del cliente.
 *
 * Estado mínimo viable para F4 del chrome global: añadir, eliminar, actualizar
 * cantidad y limpiar. Sin checkout — eso lo maneja F-shop con Wompi o el flujo
 * de pago real. Aquí solo persistimos la intención de compra.
 *
 * Persistencia manual via localStorage (`ceciluz_cart_v1`) siguiendo el
 * patrón de `useMockPayments`: NO usamos el middleware `persist` de zustand
 * para mantener trivial la lectura/escritura controlada y permitir que tests
 * unitarios manipulen el storage sin depender del middleware.
 *
 * Shape persistido en localStorage:
 *   {
 *     version: 1,
 *     items: CartItem[]
 *   }
 *
 * El versionado permite invalidar el storage si cambia el shape sin romper
 * la app del cliente. Si la versión persistida no coincide, descartamos.
 */
import { useEffect, useMemo } from 'react';
import { create } from 'zustand';

// ---------------------------------------------------------------------------
// Tipos públicos
// ---------------------------------------------------------------------------

export interface CartItem {
  /** Identificador único dentro del carrito (uuid o slug+timestamp). */
  id: string;
  /** Slug del producto para enlazar al detalle (`/tienda/:slug`). */
  productSlug: string;
  /** Título legible para mostrar en la lista. */
  title: string;
  /** Precio unitario en pesos colombianos (entero). */
  priceCop: number;
  /** Cantidad seleccionada (entero ≥ 1). */
  qty: number;
  /** ISO timestamp del momento en que se añadió. */
  addedAt: string;
}

/** Input para añadir un item al carrito. Genera `id` y `addedAt` si faltan. */
export interface AddCartItemInput {
  id?: string;
  productSlug: string;
  title: string;
  priceCop: number;
  qty?: number;
}

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'ceciluz_cart_v1';
const STORAGE_VERSION = 1;

// ---------------------------------------------------------------------------
// Helpers internos (persistencia)
// ---------------------------------------------------------------------------

interface StoredPayload {
  version: number;
  items: CartItem[];
}

/** Lee el payload persistido. Resistente a JSON corrupto / versión vieja. */
function readPersisted(): CartItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Partial<StoredPayload>;
    if (parsed.version !== STORAGE_VERSION) return [];
    if (!Array.isArray(parsed.items)) return [];
    // Saneamos cada item — descartamos los que falten campos requeridos.
    return parsed.items.filter(isValidItem);
  } catch {
    return [];
  }
}

/** Escribe el payload. Falla en silencio si el storage está lleno o bloqueado. */
function writePersisted(items: CartItem[]): void {
  if (typeof window === 'undefined') return;
  try {
    const payload: StoredPayload = { version: STORAGE_VERSION, items };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota/seguridad: ignoramos.
  }
}

function isValidItem(value: unknown): value is CartItem {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.productSlug === 'string' &&
    typeof v.title === 'string' &&
    typeof v.priceCop === 'number' &&
    typeof v.qty === 'number' &&
    v.qty >= 1 &&
    typeof v.addedAt === 'string'
  );
}

/** Genera un id estable cuando no se provee uno (uuid corto). */
function generateItemId(productSlug: string): string {
  const slugShort = productSlug.replace(/[^a-z0-9]/gi, '').slice(0, 8).toLowerCase();
  const rand = Math.random().toString(36).slice(2, 8).padEnd(6, '0');
  return `${slugShort || 'item'}-${rand}`;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

interface CartState {
  items: CartItem[];
  /** Añade un item. Si ya existe `productSlug`, suma cantidades en el primero. */
  addItem: (input: AddCartItemInput) => string;
  /** Elimina un item por id. */
  removeItem: (id: string) => void;
  /** Actualiza la cantidad. `qty <= 0` => elimina. */
  updateQty: (id: string, qty: number) => void;
  /** Limpia el carrito completo. */
  clear: () => void;
  /** Hidrata el estado desde localStorage. Idempotente. */
  hydrateFromStorage: () => void;
}

export const useCartStore = create<CartState>((set, get) => ({
  items: [],

  addItem: (input) => {
    const qty = Math.max(1, Math.floor(input.qty ?? 1));
    let assignedId = input.id ?? '';
    set((s) => {
      // Si ya existe un item con el mismo `productSlug`, sumamos cantidad
      // en lugar de duplicar la fila — UX típica de carrito.
      const existing = s.items.find((it) => it.productSlug === input.productSlug);
      if (existing) {
        assignedId = existing.id;
        const next = s.items.map((it) =>
          it.id === existing.id ? { ...it, qty: it.qty + qty } : it,
        );
        writePersisted(next);
        return { items: next };
      }

      const id = input.id ?? generateItemId(input.productSlug);
      assignedId = id;
      const item: CartItem = {
        id,
        productSlug: input.productSlug,
        title: input.title,
        priceCop: input.priceCop,
        qty,
        addedAt: new Date().toISOString(),
      };
      const next = [...s.items, item];
      writePersisted(next);
      return { items: next };
    });
    return assignedId;
  },

  removeItem: (id) => {
    set((s) => {
      const next = s.items.filter((it) => it.id !== id);
      writePersisted(next);
      return { items: next };
    });
  },

  updateQty: (id, qty) => {
    set((s) => {
      // qty <= 0 ⇒ comportamiento de remove.
      if (qty <= 0) {
        const next = s.items.filter((it) => it.id !== id);
        writePersisted(next);
        return { items: next };
      }
      const safeQty = Math.floor(qty);
      const next = s.items.map((it) =>
        it.id === id ? { ...it, qty: safeQty } : it,
      );
      writePersisted(next);
      return { items: next };
    });
  },

  clear: () => {
    writePersisted([]);
    set({ items: [] });
  },

  hydrateFromStorage: () => {
    const persisted = readPersisted();
    // Evitamos un re-render innecesario si ya está hidratado con lo mismo.
    if (get().items.length === 0 && persisted.length === 0) return;
    set({ items: persisted });
  },
}));

// ---------------------------------------------------------------------------
// Hook público — encapsula hidratación + selectores derivados.
// ---------------------------------------------------------------------------

export interface UseCartResult {
  items: CartItem[];
  itemCount: number;
  subtotalCop: number;
  addItem: CartState['addItem'];
  removeItem: CartState['removeItem'];
  updateQty: CartState['updateQty'];
  clear: CartState['clear'];
}

/**
 * Hook listo para usar desde componentes. Hidrata el store con lo que haya
 * en localStorage la primera vez que se monta y expone selectores
 * memoizados de `itemCount` y `subtotalCop`.
 *
 * `itemCount` = suma de cantidades (no de filas distintas).
 * `subtotalCop` = sumatoria `priceCop * qty` por item.
 */
export function useCart(): UseCartResult {
  const items = useCartStore((s) => s.items);
  const addItem = useCartStore((s) => s.addItem);
  const removeItem = useCartStore((s) => s.removeItem);
  const updateQty = useCartStore((s) => s.updateQty);
  const clear = useCartStore((s) => s.clear);
  const hydrateFromStorage = useCartStore((s) => s.hydrateFromStorage);

  // Hidratación una vez al montar el primer consumidor. Zustand comparte
  // el estado global, así que múltiples mounts no causan trabajo extra.
  useEffect(() => {
    hydrateFromStorage();
  }, [hydrateFromStorage]);

  // Derivados memoizados — recálculo solo cuando cambia la referencia de items.
  const { itemCount, subtotalCop } = useMemo(() => {
    let count = 0;
    let subtotal = 0;
    for (const it of items) {
      count += it.qty;
      subtotal += it.priceCop * it.qty;
    }
    return { itemCount: count, subtotalCop: subtotal };
  }, [items]);

  return {
    items,
    itemCount,
    subtotalCop,
    addItem,
    removeItem,
    updateQty,
    clear,
  };
}

/**
 * Selector ligero — devuelve sólo `itemCount`. Útil en el badge del Navbar,
 * donde no se quiere re-renderizar por cualquier mutación de la lista.
 */
export function useCartItemCount(): number {
  return useCartStore((s) =>
    s.items.reduce((acc, it) => acc + it.qty, 0),
  );
}
